'use client';

import { useId } from 'react';
import { cn } from '@/lib/utils';

/**
 * The Vale mark as the product's "Vale AI is working" glyph (VALE-1221).
 *
 * Inlines the three filled paths of `public/Vale_Favicon_Black.svg` (no
 * strokes) with the asset's hard-coded `#1C1D1F` replaced by the AI purple
 * token, so it can sit anywhere a lucide spinner or sparkle sits today: a
 * processing hero, an inline status line, a chip, a button.
 *
 * **Thinking:** each piece pulses opacity 0.2 → 1 → 0.2 over 1.5 s
 * ease-in-out, staggered 0 / 180 / 360 ms, peaking at 45% of the keyframe so
 * every pulse trails off (`vale-ai-pulse` in `app/globals.css`). Only opacity
 * is animated, never `fill`, a fill change forces a repaint on every frame.
 * Resting is the plain solid mark. `prefers-reduced-motion` stops the
 * animation and holds opacity at 1.
 *
 * **Structure:** the path data appears once, in `<defs>`, and is drawn three
 * times through `<use>`. SVG ids are document-global and collide the moment
 * two instances render on one page, so they are namespaced with `useId()`.
 * `transform-box: fill-box; transform-origin: center` sits on the drawn
 * pieces so any future transform pivots on the piece, not the canvas.
 *
 * **Tile:** a rounded square washed with the mark's colour at 10%, the mark
 * at ~62% of the tile's size.
 */
export interface ValeAILogoProps {
  /** Rendered size in px. With `tile`, the tile's size (the mark is ~62% of it). */
  size?: number;
  /** Pulse the three pieces in turn. Off = the plain solid mark. */
  thinking?: boolean;
  /** Sit the mark in a rounded square washed with its colour at 10%. */
  tile?: boolean;
  /** Any CSS colour. Defaults to the AI purple token. */
  color?: string;
  /** Accessible name. Without one the glyph is decorative and hidden from AT. */
  label?: string;
  className?: string;
}

/** `public/Vale_Favicon_Black.svg`, verbatim (viewBox 0 0 1500 1500). */
export const PIECES = [
  'M734.601 974.8C673.601 904 574.001 749.7 540.801 702.1C528.601 684.7 514.001 671.1 500.401 658.4C474.601 634.4 440.101 616.2 403.501 596.9C353.201 570.4 296.901 539.8 249.901 488.2C248.701 556.1 264.101 634.7 297.801 724C414.901 1034.4 691.001 1288.3 749.601 1288.3C774.801 1288.3 857.201 1232.7 960.601 1113.7C960.601 1113.7 951.901 1118.9 936.501 1118.1C843.801 1113.7 762.601 1001.2 734.601 974.8Z',
  'M1249.2 488.8C1249.2 488.6 1249.2 488.4 1249.2 488.2C1202.3 539.9 1146 570.5 1095.6 596.9C1059 616.2 1024.4 634.4 998.7 658.4C998.5 658.6 998.2 658.8 998 659.1C996.3 660.7 994.8 662.5 993.2 664.1C981.4 675.2 969.2 687.2 958.8 702.1C957.3 704.3 955.5 706.9 953.7 709.5C947.8 717.5 942.3 725.8 936.9 734.2C900 789.1 839 880.7 790.4 943.5C816.7 973.1 909.7 1050.9 958 1047.7C965.4 1047.2 973.2 1045.9 981 1043.8C1012 1035.5 1041.1 1013.5 1062.3 983C1086.8 947.5 1109 912.1 1129 876.8C1129.2 876.8 1129.5 876.8 1129.7 876.8C1157.1 828.1 1181.9 777 1201.9 724.1C1235.6 634.8 1251 556.2 1249.8 488.3C1249.5 488.4 1249.3 488.6 1249.2 488.8Z',
  'M1215.6 222C1214.4 218.3 1213.2 214.7 1211.8 211C1149.2 224.3 978.901 246.6 874.501 351.2C795.101 434 762.601 550.5 752.801 666.1C751.201 684.4 749.601 703 749.601 721.4C749.601 703 747.901 684.4 746.401 666.1C736.501 550.5 704.101 434 624.601 351.2C520.101 246.6 349.801 224.4 287.201 211.1C285.801 214.7 284.601 218.4 283.401 222.1C259.201 298.7 276.001 389 326.501 449.1C392.101 527 495.201 545.5 567.701 613.2C606.001 648.9 638.501 692 667.101 737C682.101 760.5 696.101 784.8 709.701 809.2C718.201 824.4 749.501 878.8 749.501 878.8C749.501 878.8 780.701 824.4 789.301 809.2C802.901 784.8 816.901 760.5 831.901 737C860.501 692 893.001 648.9 931.401 613.1C1003.9 545.4 1107 526.9 1172.6 449C1223 389 1239.9 298.6 1215.6 222Z'
];

const STAGGER_MS = [0, 180, 360];
const MARK_IN_TILE = 0.62;
const TILE_RADIUS = 0.24;
const AI_PURPLE = 'var(--color-ai)';

export function ValeAILogo({
  size = 40,
  thinking = false,
  tile = false,
  color,
  label,
  className
}: ValeAILogoProps) {
  // React's ids carry punctuation (`:r1:` / `_r_1_`); keep them id-safe.
  const id = `vale-ai-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const ink = color ?? AI_PURPLE;
  // `color="currentColor"` means "take whatever the site sets", so it must NOT
  // become an inline `color`. An inline style beats the element's own class, and
  // `color: currentColor` on the element setting it resolves to the INHERITED
  // colour, so a site writing `<ValeAIMark className="text-ai" />` would silently
  // render its parent's ink instead of purple (VALE-1251).
  const inheritsInk = ink === 'currentColor';
  const markSize = tile ? Math.round(size * MARK_IN_TILE) : size;

  const mark = (
    <svg
      width={markSize}
      height={markSize}
      viewBox="0 0 1500 1500"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('shrink-0 overflow-visible', !tile && className)}
      style={inheritsInk ? undefined : { color: ink }}
    >
      <defs>
        {PIECES.map((d, i) => (
          <path key={d} id={`${id}-${i}`} d={d} />
        ))}
      </defs>
      {PIECES.map((d, i) => (
        <use
          key={d}
          href={`#${id}-${i}`}
          className={cn(
            'origin-center [transform-box:fill-box]',
            thinking &&
              'animate-vale-ai-pulse motion-reduce:animate-none motion-reduce:opacity-100'
          )}
          style={
            thinking ? { animationDelay: `${STAGGER_MS[i]}ms` } : undefined
          }
        />
      ))}
    </svg>
  );

  if (!tile) return mark;

  return (
    <span
      className={cn('inline-grid shrink-0 place-items-center', className)}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * TILE_RADIUS),
        backgroundColor: `color-mix(in oklab, ${ink} 10%, transparent)`
      }}
    >
      {mark}
    </span>
  );
}
